import { useState, useEffect, useMemo, useCallback } from 'react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { http } from '../auth/http';
import type { User, AccessGroup, Permission } from '../types';

const pageContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem'
};

const mainContentStyle: React.CSSProperties = {
  display: 'flex',
  gap: '1.5rem',
  alignItems: 'flex-start',
};

const listContainerStyle: React.CSSProperties = {
  flex: '0 0 300px',
  backgroundColor: 'var(--surface-primary)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-md)',
  overflow: 'hidden',
};

const listHeaderStyle: React.CSSProperties = {
  padding: '1rem 1.5rem',
  borderBottom: '1px solid var(--border-color)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const listTitleStyle: React.CSSProperties = {
  fontSize: '1.125rem',
  margin: 0,
  fontWeight: 600
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  maxHeight: '60vh',
  overflowY: 'auto'
};

const listItemStyle: React.CSSProperties = {
  padding: '1rem 1.5rem',
  cursor: 'pointer',
  borderBottom: '1px solid var(--border-color)',
  transition: 'background-color 0.2s',
  fontSize: '0.9rem'
};

const detailContainerStyle: React.CSSProperties = {
  flex: 1,
  backgroundColor: 'var(--surface-primary)',
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'var(--shadow-md)',
  padding: '1.5rem 2rem',
};

const permissionTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.75rem 1rem',
  borderBottom: '2px solid var(--border-color)',
  fontSize: '0.8rem',
  textTransform: 'uppercase',
  color: 'var(--text-secondary)'
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderBottom: '1px solid var(--border-color)',
};

const checkboxLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
};

const allTableNames = [
    'beneficiario_CA001_BENEFICIARIO', 'prestador_CA001_PRESTADOR',
    'plano_CA001_PLANO', 'servico_CA001_SERVICO',
    'seguranca_CA001_USUARIOS', 'seguranca_CA002_GRUPOS_ACESSO',
    'seguranca_CA003_USUARIOS_GRUPOS', 'seguranca_CA004_PERMISSOES',
    'localizacao_CA001_REGIAO_PAIS', 'localizacao_CA002_SUB_REGIAO_PAIS',
    'localizacao_CA003_REGIAO_INTERMEDIARIA_PAIS', 'localizacao_CA004_PAIS',
    'localizacao_CA005_GRANDE_REGIAO', 'localizacao_CA006_UNIDADE_FEDERACAO',
    'localizacao_CA007_MACRO_REGIAO_SAUDE', 'localizacao_CA008_REGIAO_SAUDE',
    'localizacao_CA009_REGIAO_INTERMEDIARIA', 'localizacao_CA010_REGIAO_IMEDIATA',
    'localizacao_CA011_MESO_REGIAO', 'localizacao_CA012_MICRO_REGIAO',
    'localizacao_CA013_MUNICIPIO', 'localizacao_CA014_DISTRITO',
    'localizacao_CA015_SUB_DISTRITO'
];

function Seguranca() {
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<AccessGroup[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<AccessGroup | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [usersData, groupsData, permissionsData] = await Promise.all([
        http.get<User[]>('/api/seguranca/usuarios'),
        http.get<AccessGroup[]>('/api/seguranca/grupos-acesso'),
        http.get<Permission[]>('/api/seguranca/permissoes')
      ]);
      setUsers(usersData);
      setGroups(groupsData);
      setPermissions(permissionsData);
      if (groupsData.length > 0) {
        setSelectedGroup(groupsData[0]);
      }
    } catch (err) {
      setError((err as Error).message || 'Falha ao carregar dados de segurança.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handlePermissionChange = (tableName: string, operation: keyof Omit<Permission, 'ideGrupoAcesso' | 'nmeTabela'>, value: boolean) => {
    if (!selectedGroup) return;

    setPermissions(prev => {
        const newPermissions = [...prev];
        const permissionIndex = newPermissions.findIndex(p => p.ideGrupoAcesso === selectedGroup.ideGrupoAcesso && p.nmeTabela === tableName);
        
        if (permissionIndex > -1) {
            newPermissions[permissionIndex] = { ...newPermissions[permissionIndex], [operation]: value ? 1 : 0 };
        } else {
            newPermissions.push({
                ideGrupoAcesso: selectedGroup.ideGrupoAcesso,
                nmeTabela: tableName,
                staSelect: 0, staInsert: 0, staUpdate: 0, staDelete: 0,
                [operation]: value ? 1 : 0
            });
        }
        return newPermissions;
    });
  };
  
  const handleSavePermissions = async () => {
    if (!selectedGroup) return;
    const groupPermissions = permissions.filter(p => p.ideGrupoAcesso === selectedGroup.ideGrupoAcesso);
    setSaveStatus('saving');
    try {
      await http.put('/api/seguranca/permissoes', groupPermissions);
      setSaveStatus('success');
      setStatusMessage('Permissões salvas com sucesso!');
    } catch (err) {
      setSaveStatus('error');
      setStatusMessage((err as Error).message || 'Falha ao salvar permissões.');
    } finally {
      setTimeout(() => {
        setSaveStatus('idle');
        setStatusMessage('');
      }, 3000);
    }
  };

  const groupPermissions = useMemo(() => {
    if (!selectedGroup) return new Map<string, Omit<Permission, 'ideGrupoAcesso' | 'nmeTabela'>>();
    
    const permsMap = new Map<string, Omit<Permission, 'ideGrupoAcesso' | 'nmeTabela'>>();
    permissions
      .filter(p => p.ideGrupoAcesso === selectedGroup.ideGrupoAcesso)
      .forEach(p => {
          permsMap.set(p.nmeTabela, { staSelect: p.staSelect, staInsert: p.staInsert, staUpdate: p.staUpdate, staDelete: p.staDelete });
      });
      
    return permsMap;

  }, [selectedGroup, permissions]);

  if (isLoading) return <div>Carregando...</div>;
  if (error) return <div className="login-error">{error}</div>;

  return (
    <div style={pageContainerStyle}>
      <Breadcrumbs currentPage="Segurança" />
      <header className="page-header">
        <h2 className="page-title">Gestão de Segurança</h2>
      </header>

      <div style={mainContentStyle}>
        <div style={listContainerStyle}>
          <div style={listHeaderStyle}>
            <h3 style={listTitleStyle}>Grupos de Acesso</h3>
          </div>
          <ul style={listStyle}>
            {groups.map(group => (
              <li 
                key={group.ideGrupoAcesso} 
                style={{...listItemStyle, backgroundColor: selectedGroup?.ideGrupoAcesso === group.ideGrupoAcesso ? 'var(--surface-secondary)' : 'transparent'}}
                onClick={() => setSelectedGroup(group)}
              >
                {group.nmeGrupoAcesso}
              </li>
            ))}
          </ul>
        </div>

        <div style={detailContainerStyle}>
          {selectedGroup ? (
            <>
              <div style={{...listHeaderStyle, padding: '0 0 1.5rem 0'}}>
                <h3 style={listTitleStyle}>Permissões para: {selectedGroup.nmeGrupoAcesso}</h3>
                <div className="btn-group">
                    {saveStatus !== 'idle' && <span className={`save-status ${saveStatus}`}>{statusMessage}</span>}
                    <button className="btn btn-primary" onClick={handleSavePermissions} disabled={saveStatus === 'saving'}>
                        {saveStatus === 'saving' ? 'Salvando...' : 'Salvar Permissões'}
                    </button>
                </div>
              </div>
              <div className='table-wrapper' style={{boxShadow: 'none'}}>
                <table style={permissionTableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Tabela</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Visualizar</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Criar</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Editar</th>
                      <th style={{...thStyle, textAlign: 'center'}}>Excluir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allTableNames.map(tableName => {
                      const currentPerms = groupPermissions.get(tableName) || { staSelect: 0, staInsert: 0, staUpdate: 0, staDelete: 0 };
                      return (
                        <tr key={tableName}>
                          <td style={tdStyle}>
                            <span style={{fontFamily: 'monospace', fontSize: '0.9rem'}}>{tableName}</span>
                          </td>
                          {(['staSelect', 'staInsert', 'staUpdate', 'staDelete'] as const).map(op => (
                            <td key={op} style={{...tdStyle, textAlign: 'center'}}>
                                <label style={checkboxLabelStyle}>
                                    <input
                                        type="checkbox"
                                        checked={currentPerms[op] === 1}
                                        onChange={(e) => handlePermissionChange(tableName, op, e.target.checked)}
                                    />
                                </label>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p>Selecione um grupo para ver as permissões.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default Seguranca;